/**
 * Série diária de pedidos e receita (C5) — pura e sem I/O.
 *
 * Não há tabela de série histórica: a série é DERIVADA dos próprios pedidos
 * unificados, agrupando por dia de criação (`placedAt`) no fuso de São Paulo
 * (o dia comercial do cliente, não o dia UTC). Por isso ela nunca inventa
 * dado: um dia sem pedido aparece com zero PORQUE não houve pedido, e a janela
 * só mostra o que as ordens existentes cobrem.
 *
 * Regras que a tela precisa saber:
 * - `orders` conta todos os pedidos criados no dia, exceto cancelados;
 * - `revenueCents` soma só os pedidos em estado de receita (`isRevenueState`),
 *   a MESMA regra dos demais painéis — pedido aguardando pagamento não é venda;
 * - a janela termina HOJE (inclusive) e tem `days` dias consecutivos.
 */
import { isRevenueState } from "./state-machine.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

export const DAILY_SERIES_WINDOWS = [7, 30] as const;
export type DailySeriesWindow = (typeof DAILY_SERIES_WINDOWS)[number];

export interface DailyPoint {
  /** Dia comercial no formato `YYYY-MM-DD` (fuso America/Sao_Paulo). */
  date: string;
  /** Rótulo curto para o eixo (`dd/MM`). */
  label: string;
  orders: number;
  revenueCents: number;
}

const DAY_FORMAT = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" });

/** Dia comercial (`YYYY-MM-DD`) de um instante, no fuso de São Paulo. */
export function businessDay(instant: Date): string {
  return DAY_FORMAT.format(instant);
}

export function dailySeries(orders: PlatformUnifiedOrder[], days: number, now: Date): DailyPoint[] {
  const points = new Map<string, DailyPoint>();
  // Retrocede em passos de 24h a partir de agora: com o fuso fixo (sem horário de verão no Brasil
  // desde 2019) cada passo cai no dia comercial anterior, sem pular nem repetir dia.
  for (let back = days - 1; back >= 0; back -= 1) {
    const date = businessDay(new Date(now.getTime() - back * 86_400_000));
    points.set(date, { date, label: `${date.slice(8, 10)}/${date.slice(5, 7)}`, orders: 0, revenueCents: 0 });
  }
  for (const order of orders) {
    if (order.state === "cancelled") continue;
    const placed = new Date(order.placedAt);
    if (Number.isNaN(placed.getTime())) continue;
    const point = points.get(businessDay(placed));
    if (!point) continue;
    point.orders += 1;
    if (isRevenueState(order.state)) point.revenueCents += order.totalCents ?? 0; // sem total declarado não soma (nunca inventa valor)
  }
  return [...points.values()];
}
